import { type FormEvent, useEffect, useRef, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  ArrowRightIcon,
  BotIcon,
  ChevronDownIcon,
  HistoryIcon,
  LoaderCircleIcon,
  MapPinIcon,
  XIcon,
} from "lucide-react";

import { SearchV2FiltersDialog } from "./SearchV2FiltersDialog";
import { SearchV2TuningPopover } from "./SearchV2TuningPopover";
import { type RecentSearchV2Request } from "./searchV2RecentQueriesStorage";

import { Column, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { useClassifyCandidateSearchFilters } from "@/hooks/mutations/useClassifyCandidateSearchFilters";
import { useGetPlaces } from "@/hooks/queries/useGetPlaces";
import {
  DEFAULT_SEARCH_WEIGHTS,
  MAX_SEARCH_V2_KEYWORDS,
  positionCategoryToLabel,
  SEARCH_V2_ANALYTICS_METADATA,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { searchV2Analytics } from "@/lib/searchV2Analytics";
import { cn } from "@/lib/utils";
import {
  ClassifiedKeyword,
  Job,
  PositionCategory,
  SallyState,
  SearchV2Filters,
  SearchV2TenureFilter,
  SearchV2Weights,
} from "@/types";

const DISTANCE_OPTIONS = ["30 mi", "50 mi", "100 mi", "Everywhere"];
const DEFAULT_SEARCH_PLACEHOLDER = "Search candidates";
const SALLY_SEARCH_PLACEHOLDER = "Search Sally sourced candidates";
const SEARCH_INTRO_STORAGE_KEY = "search-v2-intro-day";
const DEFAULT_SEARCH_INPUT_EXAMPLE =
  "CNC Machinist whose worked with mazak machines";
const positionCategoryToSearchExample: Record<PositionCategory, string> = {
  [PositionCategory.PRODUCTION]:
    "Assembler with experience on fast-paced production lines",
  [PositionCategory.MACHINING]:
    "CNC Machinist who has worked with mazak machines",
  [PositionCategory.WELDING]: "MIG Welder who has worked with stainless steel",
  [PositionCategory.MAINTENANCE]:
    "Maintenance Tech who has worked on hydraulic systems",
  [PositionCategory.ENGINEERING]:
    "Manufacturing Engineer who has worked with lean processes",
};
const TYPEWRITER_CHARACTER_DELAY_MS = 68;
const TYPEWRITER_START_DELAY_MS = 400;
const TYPEWRITER_WORD_DELAY_MS = 220;

export const SearchV2Header = ({
  filters,
  hiddenUserCount,
  interestedCandidateCount,
  isSallyMode,
  job,
  onClearHiddenUsers,
  onFiltersChange,
  onRadiusChange,
  onRecentSearchSelect,
  onReviewAll,
  onSallyStatesChange,
  onToggleSallyMode,
  onWeightsChange,
  radius,
  recentSearches,
  topCandidateCount,
  sallyStates,
  weights,
}: {
  filters: SearchV2Filters;
  hiddenUserCount: number;
  interestedCandidateCount: number;
  isSallyMode: boolean;
  job: Job;
  onClearHiddenUsers: () => void;
  onToggleSallyMode: () => void;
  onFiltersChange: (filters: SearchV2Filters) => void;
  onRadiusChange: (radius: string) => void;
  onRecentSearchSelect: (recentSearch: RecentSearchV2Request) => void;
  onReviewAll: () => void;
  onSallyStatesChange: (states: SallyState[]) => void;
  recentSearches: RecentSearchV2Request[];
  topCandidateCount: number;
  onWeightsChange: (weights: SearchV2Weights) => void;
  radius: string;
  sallyStates: SallyState[];
  weights: SearchV2Weights;
}) => {
  /* Database CRUD Operations */
  // Update
  const {
    isPending: isClassifyingSearch,
    mutate: classifyCandidateSearchFilters,
  } = useClassifyCandidateSearchFilters();

  /* React Hooks */
  const [query, setQuery] = useState("");
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [locationDraft, setLocationDraft] = useState("");
  const [isRecentSearchesOpen, setIsRecentSearchesOpen] = useState(false);
  const [debouncedLocationDraft] = useDebounce(locationDraft, 250);
  const { data: placesData } = useGetPlaces(
    isLocationOpen ? debouncedLocationDraft.trim() : "",
  );
  const [searchPlaceholder, setSearchPlaceholder] = useState(
    DEFAULT_SEARCH_PLACEHOLDER,
  );
  const [isSearchIntroActive, setIsSearchIntroActive] = useState(false);
  const [isTypingSearchExample, setIsTypingSearchExample] = useState(false);
  const hasCheckedSearchIntro = useRef(false);
  const isSearchExampleActive = useRef(true);
  const searchInputExample = job.positionCategory
    ? positionCategoryToSearchExample[job.positionCategory]
    : DEFAULT_SEARCH_INPUT_EXAMPLE;

  // Checks if we have done the flashy search animation once a day already
  useEffect(() => {
    if (hasCheckedSearchIntro.current) {
      return;
    }

    hasCheckedSearchIntro.current = true;
    const today = dates.localDayKey(new Date());
    try {
      if (window.localStorage.getItem(SEARCH_INTRO_STORAGE_KEY) === today) {
        return;
      }

      window.localStorage.setItem(SEARCH_INTRO_STORAGE_KEY, today);
    } catch {
      // Show the intro when storage is unavailable rather than blocking search.
    }
    setIsSearchIntroActive(true);
  }, []);

  useEffect(() => {
    if (!isSearchIntroActive) {
      return;
    }

    isSearchExampleActive.current = true;
    setIsTypingSearchExample(true);
    setSearchPlaceholder("");
    let characterIndex = 0;
    let typingTimer: number | undefined;
    const typeNextCharacter = () => {
      if (!isSearchExampleActive.current) {
        return;
      }

      characterIndex += 1;
      setSearchPlaceholder(searchInputExample.slice(0, characterIndex));
      if (characterIndex === searchInputExample.length) {
        setIsTypingSearchExample(false);
        return;
      }

      const typedCharacter = searchInputExample[characterIndex - 1];
      typingTimer = window.setTimeout(
        typeNextCharacter,
        typedCharacter === " "
          ? TYPEWRITER_WORD_DELAY_MS
          : TYPEWRITER_CHARACTER_DELAY_MS,
      );
    };

    typingTimer = window.setTimeout(
      typeNextCharacter,
      TYPEWRITER_START_DELAY_MS,
    );

    return () => {
      if (typingTimer !== undefined) {
        window.clearTimeout(typingTimer);
      }
    };
  }, [isSearchIntroActive, searchInputExample]);

  /* Helpers */
  const searchChips = filters.keywords;

  /* Handlers */
  // One analytics event per changed filter field; keywords are capped at
  // MAX_SEARCH_V2_KEYWORDS no matter where they come from.
  const handleFiltersChange = (
    filtersUpdate: SearchV2Filters,
    source: "manual" | "nl_classifier" = "manual",
  ) => {
    const seenKeywords = new Set<string>();
    const nextFilters = {
      ...filtersUpdate,
      keywords: filtersUpdate.keywords
        .filter(({ keyword }) => {
          const normalizedKeyword = keyword.trim().toLowerCase();
          if (!normalizedKeyword || seenKeywords.has(normalizedKeyword)) {
            return false;
          }
          seenKeywords.add(normalizedKeyword);
          return true;
        })
        .map((classifiedKeyword) => ({
          ...classifiedKeyword,
          keyword: classifiedKeyword.keyword.trim(),
        }))
        .slice(0, MAX_SEARCH_V2_KEYWORDS),
    };
    searchV2Analytics.trackFilterChanges({
      jobId: job.id,
      nextFilters,
      previousFilters: filters,
      source,
    });
    onFiltersChange(nextFilters);
  };

  const handleSearch = () => {
    const naturalLanguageQuery = query.trim();
    if (!naturalLanguageQuery) {
      return;
    }
    mixpanelUtils.trackButtonClick("Search candidates", MIXPANEL_SCREENS.JOB, {
      ...SEARCH_V2_ANALYTICS_METADATA,
      job_id: job.id,
      query_length: naturalLanguageQuery.length,
    });
    classifyCandidateSearchFilters(
      { query: naturalLanguageQuery },
      {
        onSuccess: (classifiedFilters) => {
          setQuery("");
          // Merge classified keywords into the existing ones (deduped); the chips
          // under the input re-derive from filters.keywords automatically.
          handleFiltersChange(
            {
              ...filters,
              keywords: [
                ...filters.keywords.filter((existingKeyword) => {
                  return !classifiedFilters.keywords?.some(
                    (classifiedKeyword) =>
                      classifiedKeyword.keyword.trim().toLowerCase() ===
                      existingKeyword.keyword.trim().toLowerCase(),
                  );
                }),
                ...(classifiedFilters.keywords ?? []),
              ],
              // The classifier still returns the wire name `payFloor`.
              payCeiling: classifiedFilters.payFloor ?? "",
              title: classifiedFilters.weTitle ?? [],
            },
            "nl_classifier",
          );
        },
      },
    );
  };

  const handleSearchChipRemove = (chip: ClassifiedKeyword) => {
    handleFiltersChange({
      ...filters,
      keywords: filters.keywords.filter(
        ({ keyword }) => keyword !== chip.keyword,
      ),
    });
  };

  const handleTitleChipRemove = (title: string) => {
    handleFiltersChange({
      ...filters,
      title: filters.title.filter((currentTitle) => currentTitle !== title),
    });
  };

  const handleQueryChange = (value: string) => {
    isSearchExampleActive.current = false;
    setIsTypingSearchExample(false);
    setSearchPlaceholder(DEFAULT_SEARCH_PLACEHOLDER);
    setQuery(value);
  };

  const handleLocationOpenChange = (open: boolean) => {
    if (open) {
      setLocationDraft(filters.location ?? "");
    }
    setIsLocationOpen(open);
  };

  // Empty value (or one matching the job's own location) clears the override
  // so the search falls back to the job's city/state.
  const handleLocationApply = (value: string) => {
    // Google predictions end in ", USA" — the search wants bare "City, ST".
    const nextLocation = value.trim().replace(/, USA$/, "");
    const jobLocation = `${job.city}, ${job.state}`;
    handleFiltersChange({
      ...filters,
      location:
        nextLocation && nextLocation !== jobLocation ? nextLocation : undefined,
    });
    setIsLocationOpen(false);
  };

  const handleRadiusChange = (nextRadius: string) => {
    searchV2Analytics.trackRadiusChange({ jobId: job.id, radius: nextRadius });
    onRadiusChange(nextRadius);
  };

  const handleRecentSearchSelect = (recentSearch: RecentSearchV2Request) => {
    setIsRecentSearchesOpen(false);
    onRecentSearchSelect(recentSearch);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSearch();
  };

  /* Render */
  const renderTitleChip = (title: string, onRemove: () => void) => {
    return (
      <Row
        className="h-7 items-center gap-1 rounded-md bg-slate-100 pl-2.5 pr-1"
        key={title}
      >
        <Text className="text-xs font-normal text-slate-600">
          Title: {title}
        </Text>
        <Button
          className="h-6 w-6 p-0 text-slate-400 hover:text-slate-700"
          onClick={() => {
            onRemove();
          }}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon className="h-3.5 w-3.5" />
        </Button>
      </Row>
    );
  };

  const renderSearchChip = (
    classifiedKeyword: ClassifiedKeyword,
    onRemove: () => void,
  ) => {
    return (
      <Row
        className="h-7 items-center gap-1 rounded-md bg-slate-100 pl-2.5 pr-1"
        key={classifiedKeyword.keyword}
      >
        <Text className="text-xs font-normal text-slate-600">
          {/* snake_case compounds read as the spaced phrase on the chip. */}
          {classifiedKeyword.keyword.replace(/_/g, " ")}
        </Text>
        <Button
          className="h-6 w-6 p-0 text-slate-400 hover:text-slate-700"
          onClick={() => {
            onRemove();
          }}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon className="h-3.5 w-3.5" />
        </Button>
      </Row>
    );
  };

  const renderFilterChip = (
    key: string,
    label: string,
    onRemove: () => void,
  ) => {
    return (
      <Row
        className="h-7 items-center gap-1 rounded-md bg-slate-100 pl-2.5 pr-1"
        key={key}
      >
        <Text className="text-xs font-normal text-slate-600">{label}</Text>
        <Button
          className="h-6 w-6 p-0 text-slate-400 hover:text-slate-700"
          onClick={onRemove}
          size="icon"
          type="button"
          variant="ghost"
        >
          <XIcon className="h-3.5 w-3.5" />
        </Button>
      </Row>
    );
  };

  const formatTenureRange = ({ ceiling, floor }: SearchV2TenureFilter) => {
    if (floor !== undefined && ceiling !== undefined) {
      return `${floor}–${ceiling} yrs`;
    }
    if (floor !== undefined) return `≥ ${floor} yrs`;
    return `≤ ${ceiling} yrs`;
  };

  const getRecentSearchMeta = (recentSearch: RecentSearchV2Request) => {
    const { filters: searchFilters, weights: searchWeights } = recentSearch;
    return [
      searchFilters.payCeiling ? `≤ $${searchFilters.payCeiling}/hr` : "",
      searchFilters.includeRejected ? "Incl. rejected" : "",
      searchFilters.includePastSubmitted ? "Incl. past submitted" : "",
      searchWeights.semanticWeight !== DEFAULT_SEARCH_WEIGHTS.semanticWeight ||
      searchWeights.activityWeight !== DEFAULT_SEARCH_WEIGHTS.activityWeight ||
      searchWeights.weRecencyWeight !== DEFAULT_SEARCH_WEIGHTS.weRecencyWeight
        ? "Tuned"
        : "",
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const renderRecentSearches = () => {
    if (!recentSearches.length) {
      return null;
    }
    return (
      <Popover
        onOpenChange={setIsRecentSearchesOpen}
        open={isRecentSearchesOpen}
      >
        <PopoverTrigger asChild>
          <Button
            className="h-8 w-8 shrink-0 border-none text-slate-600 hover:text-slate-700"
            size="icon"
            title="Recent searches"
            type="button"
            variant="outline"
          >
            <HistoryIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[560px] rounded-xl p-0 py-1">
          <Column>
            <Text className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Recent searches
            </Text>
            <Column className="divide-y divide-slate-100">
              {recentSearches.map((recentSearch, index) => (
                <Button
                  className="h-auto justify-start gap-3 rounded-none px-4 py-3 text-left hover:bg-slate-50"
                  key={`recent-search-${String(index)}`}
                  onClick={() => {
                    handleRecentSearchSelect(recentSearch);
                  }}
                  type="button"
                  variant="ghost"
                >
                  <HistoryIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 self-start text-slate-400" />
                  <Column className="min-w-0 flex-1 gap-1">
                    {recentSearch.filters.title.length > 0 && (
                      <Text className="truncate text-sm font-medium text-slate-800">
                        {recentSearch.filters.title.join(", ")}
                      </Text>
                    )}
                    {recentSearch.filters.keywords.length > 0 && (
                      <Text className="truncate text-xs font-normal text-slate-500">
                        {recentSearch.filters.keywords
                          .map(({ keyword }) => keyword)
                          .join(", ")}
                      </Text>
                    )}
                    {getRecentSearchMeta(recentSearch) ? (
                      <Text className="text-xs font-normal text-slate-400">
                        {getRecentSearchMeta(recentSearch)}
                      </Text>
                    ) : null}
                  </Column>
                </Button>
              ))}
            </Column>
          </Column>
        </PopoverContent>
      </Popover>
    );
  };

  // Lives inside the input because the mode changes what the box searches, not
  // how results are narrowed — the same reason it isn't one of the filters.
  const renderSallyToggle = () => {
    return (
      <Button
        className={cn(
          "absolute right-11 h-7 w-[52px] shrink-0 rounded-full p-0 transition-colors",
          isSallyMode
            ? "bg-blue-600 hover:bg-blue-600"
            : "bg-slate-200 hover:bg-slate-300",
        )}
        onClick={onToggleSallyMode}
        title={
          isSallyMode
            ? "Searching the candidates Sally sourced for this job"
            : "Search only the candidates Sally sourced for this job"
        }
        type="button"
        variant="ghost"
      >
        <Row
          className={cn(
            "h-[22px] w-[22px] items-center justify-center rounded-full bg-white shadow transition-transform",
            isSallyMode ? "translate-x-[11px]" : "-translate-x-[11px]",
          )}
        >
          <BotIcon
            className={cn(
              "h-3.5 w-3.5",
              isSallyMode ? "text-blue-600" : "text-slate-400",
            )}
          />
        </Row>
      </Button>
    );
  };

  const renderSearchForm = () => {
    return (
      <form onSubmit={handleSubmit}>
        <Row className="items-center gap-3">
          <Row
            className={cn(
              "relative min-w-0 flex-1 items-center",
              "search-input-trace",
            )}
          >
            <Input
              className={cn(
                "h-12 rounded-2xl border-slate-300 pr-[104px] shadow-sm placeholder:text-slate-400",
                isTypingSearchExample && "placeholder:font-plex-mono",
                isSallyMode &&
                  "border-blue-400 ring-4 ring-blue-100 placeholder:text-blue-600",
              )}
              onChange={(event) => {
                handleQueryChange(event.target.value);
              }}
              placeholder={
                isSallyMode ? SALLY_SEARCH_PLACEHOLDER : searchPlaceholder
              }
              value={query}
            />
            {renderSallyToggle()}
            <Button
              className="absolute right-2 h-8 w-8 rounded-full p-0 text-blue-600"
              disabled={isClassifyingSearch}
              size="icon"
              type="submit"
              variant="ghost"
            >
              {isClassifyingSearch ? (
                <LoaderCircleIcon className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRightIcon className="h-4 w-4" />
              )}
            </Button>
          </Row>
          {renderRecentSearches()}
          {/* Tuning weights never reach the Sally corpus (byIds ranks
              text-only), so the sliders would silently no-op there. */}
          {isSallyMode ? null : (
            <SearchV2TuningPopover
              jobId={job.id}
              onWeightsChange={onWeightsChange}
              weights={weights}
            />
          )}
          <SearchV2FiltersDialog
            filters={filters}
            hiddenUserCount={hiddenUserCount}
            isSallyMode={isSallyMode}
            onApply={handleFiltersChange}
            onClearHiddenUsers={onClearHiddenUsers}
            onSallyStatesChange={onSallyStatesChange}
            sallyStates={sallyStates}
          />
        </Row>
      </form>
    );
  };

  const renderSallyBanner = () => {
    if (!isSallyMode) {
      return null;
    }
    return (
      <Row className="h-7 min-w-0 shrink-0 items-center gap-2 rounded-lg bg-blue-50/70 px-3">
        <BotIcon className="h-4 w-4 shrink-0 text-blue-600" />
        <Text className="truncate text-sm font-normal text-slate-600">
          Limits candidates to those identified by Sally to be{" "}
          <span className="font-semibold">Great</span> or{" "}
          <span className="font-semibold">Excellent</span>.
        </Text>
      </Row>
    );
  };

  const renderLocationEditor = () => {
    const places = placesData ?? [];
    return (
      <Column className="gap-2">
        <Text className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Search location
        </Text>
        <Input
          autoFocus
          onChange={(event) => {
            setLocationDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleLocationApply(locationDraft);
            }
          }}
          placeholder="Begin typing in a location..."
          value={locationDraft}
        />
        {places.length > 0 ? (
          <Column className="max-h-56 overflow-y-auto">
            {places.map((place) => (
              <Button
                className="h-8 justify-start px-2 text-sm font-normal text-slate-700"
                key={place}
                onClick={() => {
                  handleLocationApply(place);
                }}
                type="button"
                variant="ghost"
              >
                <MapPinIcon className="h-3.5 w-3.5 text-slate-400" />
                {place}
              </Button>
            ))}
          </Column>
        ) : null}
        {filters.location ? (
          <Button
            className="h-7 justify-start px-2 text-xs font-normal text-slate-500 hover:text-slate-700"
            onClick={() => {
              handleLocationApply("");
            }}
            size="sm"
            type="button"
            variant="ghost"
          >
            <XIcon className="h-3 w-3" />
            Reset to job location ({job.city}, {job.state})
          </Button>
        ) : null}
      </Column>
    );
  };

  const renderLocationAndChips = () => {
    return (
      <Row className="items-start gap-3">
        <Row className="min-w-0 flex-1 items-start gap-3">
          {/* Location + radius shape a discovery search; in Sally mode the
              corpus is fixed, so both would
              silently no-op. The banner takes their place — it answers "where
              did the filters go" — and keyword/title chips stay: they rank. */}
          {isSallyMode ? (
            renderSallyBanner()
          ) : (
            <>
              <Popover
                onOpenChange={handleLocationOpenChange}
                open={isLocationOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    className={cn(
                      "h-7 shrink-0 gap-1.5 px-2 text-sm font-normal text-slate-700 hover:bg-slate-100 hover:text-slate-800",
                      filters.location && "text-blue-700 hover:text-blue-800",
                    )}
                    title="Change search location"
                    type="button"
                    variant="ghost"
                  >
                    <MapPinIcon
                      className={cn(
                        "h-3.5 w-3.5 text-slate-400",
                        filters.location && "text-blue-600",
                      )}
                    />
                    {filters.location ?? `${job.city}, ${job.state}`}
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-80 rounded-xl p-3">
                  {renderLocationEditor()}
                </PopoverContent>
              </Popover>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="h-7 gap-1.5 border-slate-200 px-2 text-xs font-normal text-slate-600 hover:bg-slate-50 hover:text-slate-700"
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    {radius}
                    <ChevronDownIcon className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-36">
                  <DropdownMenuRadioGroup
                    onValueChange={handleRadiusChange}
                    value={radius}
                  >
                    {DISTANCE_OPTIONS.map((option) => (
                      <DropdownMenuRadioItem key={option} value={option}>
                        {option}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <Row className="h-5 w-px shrink-0 bg-slate-200" />
            </>
          )}
          <Row className="min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {filters.title.map((title) =>
              renderTitleChip(title, () => {
                handleTitleChipRemove(title);
              }),
            )}
            {searchChips.map((chip) =>
              renderSearchChip(chip, () => {
                handleSearchChipRemove(chip);
              }),
            )}
            {filters.payCeiling
              ? renderFilterChip(
                  "pay-ceiling",
                  `Pay: ≤ $${filters.payCeiling}/hr`,
                  () => {
                    handleFiltersChange({ ...filters, payCeiling: "" });
                  },
                )
              : null}
            {filters.tenure
              ? renderFilterChip(
                  "tenure",
                  `Tenure: ${formatTenureRange(filters.tenure)} in ${positionCategoryToLabel[filters.tenure.category]}`,
                  () => {
                    handleFiltersChange({ ...filters, tenure: null });
                  },
                )
              : null}
            {filters.includeRejected
              ? renderFilterChip(
                  "include-rejected",
                  "Including rejected",
                  () => {
                    handleFiltersChange({ ...filters, includeRejected: false });
                  },
                )
              : null}
            {filters.includePastSubmitted
              ? renderFilterChip(
                  "include-past-submitted",
                  "Including placed & past submitted",
                  () => {
                    handleFiltersChange({
                      ...filters,
                      includePastSubmitted: false,
                    });
                  },
                )
              : null}
          </Row>
        </Row>
        <Row className="ml-auto shrink-0 items-center gap-3">
          {topCandidateCount > 0 || interestedCandidateCount > 0 ? (
            <Button
              className="h-9 px-4 font-normal"
              onClick={onReviewAll}
              type="button"
            >
              {interestedCandidateCount > 0
                ? `Review ${String(topCandidateCount + interestedCandidateCount)}`
                : "Review Top Candidates"}
            </Button>
          ) : null}
        </Row>
      </Row>
    );
  };

  const renderSearchDetails = () => {
    return (
      <Column className="gap-5 border-b border-slate-200 pb-4">
        {renderLocationAndChips()}
      </Column>
    );
  };

  const render = () => {
    return (
      <Column className="gap-7">
        {renderSearchForm()}
        {renderSearchDetails()}
      </Column>
    );
  };

  return render();
};
